import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getActingOrganizationId } from '../utils/actingOrganization';
import { getImpersonationIdentityKey } from '../utils/apiConfig';

/**
 * Direct links to a product, staged product or product issue (flag
 * `product_permalinks`, MOT-833).
 *
 * One cache entry per item, shared by every opener: the list row that was
 * clicked seeds it, a refresh or a shared link fills it with a single by-id
 * read (never a list or SKU search), and it is trusted for
 * `PERMALINK_STALE_MS` before the next read.
 *
 * The by-id fetchers are the existing guarded routes (`GET /products/:id`,
 * `/normalization/canonical/:id`, `/staged-products/:id`,
 * `/product-issues/:id`), so what a caller may open is decided by the server,
 * never by the URL. A seed is only ever a row that same caller already holds.
 */
export const PERMALINK_STALE_MS = 120_000;

export type PermalinkScope = 'catalog' | 'canonical' | 'staged' | 'issue';

/**
 * Staged products and issues live under their list's own key prefix, so every
 * `invalidateQueries(['staged-products'])` / `(['product-issues'])` an edit
 * already makes also marks the open item stale and re-reads it.
 */
export const permalinkKey = (scope: PermalinkScope, id: string, identity = '') =>
  scope === 'staged'
    ? (['staged-products', 'permalink', id, identity] as const)
    : scope === 'issue'
      ? (['product-issues', 'permalink', id, identity] as const)
      : (['product-permalink', scope, id, identity] as const);

/** Thrown by a by-id fetcher when the server says the item does not exist or is not the caller's to open. */
export class PermalinkNotFoundError extends Error {
  constructor(message = 'Not found') {
    super(message);
    this.name = 'PermalinkNotFoundError';
  }
}

/** True for a 403/404 from axios or fetch-style errors: the item is not there for this caller. */
export const isNotFoundStatus = (status: number | undefined): boolean => status === 404 || status === 403;

export const statusOfError = (error: unknown): number | undefined => {
  const e = error as { response?: { status?: number }; status?: number } | null;
  return e?.response?.status ?? e?.status;
};

interface PermalinkOptions<T> {
  /** The `product_permalinks` flag. Off: nothing is read. */
  enabled: boolean;
  scope: PermalinkScope;
  /** The id in the address, or empty when the list is showing. */
  routeId: string;
  fetchById: (id: string) => Promise<T>;
}

/** Reads the item named by the address once, from the shared cache when it is there. */
export function useProductPermalink<T>({ enabled, scope, routeId, fetchById }: PermalinkOptions<T>) {
  const queryClient = useQueryClient();
  // The query client outlives a sign-out, so who is asking is part of the key: a product one person
  // could open (a store's own private upload) is never served from the cache to the next person.
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const identity = `${uid ?? 'anon'}:${getActingOrganizationId(uid) ?? ''}:${getImpersonationIdentityKey()}`;
  const active = enabled && !!routeId;
  const query = useQuery<T>({
    queryKey: permalinkKey(scope, routeId, identity),
    queryFn: () => fetchById(routeId),
    enabled: active,
    staleTime: PERMALINK_STALE_MS,
    retry: false,
  });

  const notFound =
    active &&
    query.isError &&
    (query.error instanceof PermalinkNotFoundError || isNotFoundStatus(statusOfError(query.error)));

  /**
   * Puts a row the caller already holds into the shared entry so the item
   * opens without waiting. `revalidate` marks it stale at once, for rows that
   * can lag a write (the staged queue after a bulk action).
   */
  const seed = useCallback(
    (id: string, item: T, revalidate = false) => {
      queryClient.setQueryData(permalinkKey(scope, id, identity), item, revalidate ? { updatedAt: 0 } : undefined);
    },
    [queryClient, scope, identity],
  );

  /** Drops the entry after an edit so the next open reads the saved item. */
  const invalidate = useCallback(
    (id?: string) =>
      queryClient.invalidateQueries({
        queryKey: id ? permalinkKey(scope, id, identity) : permalinkKey(scope, '', '').slice(0, 2),
      }),
    [queryClient, scope, identity],
  );

  return {
    // A 403/404 wins over anything cached or seeded: an item the server will not serve is not shown.
    item: active && !notFound ? query.data : undefined,
    isLoading: active && query.isLoading,
    isFetching: active && query.isFetching,
    notFound,
    /** A failure that is not "not found" (server, network): the linked item did not open, and a retry may. */
    loadFailed: active && query.isError && !notFound,
    seed,
    invalidate,
    refetch: query.refetch,
  };
}

/**
 * Open and close for a permalink page. Opening pushes the item's address on
 * top of the list's own (filters kept in the query string), so Back closes
 * it; closing steps back when the item was opened from the list, and
 * otherwise (a shared link, a refresh) replaces the address with the list.
 */
export function usePermalinkNavigation(basePath: string, enabled: boolean) {
  const navigate = useNavigate();
  const location = useLocation();

  const addressOf = useCallback(
    (id: string) => ({
      pathname: `${basePath}/${encodeURIComponent(id)}`,
      search: location.search,
    }),
    [basePath, location.search],
  );

  /** Pushes `id`; `replace` swaps the open item instead (variant switch, linked product). */
  const open = useCallback(
    (id: string, options: { replace?: boolean } = {}) => {
      if (!enabled || !id) return;
      navigate(addressOf(id), {
        replace: options.replace === true,
        state: options.replace ? location.state : { permalinkFromList: true },
      });
    },
    [enabled, navigate, addressOf, location.state],
  );

  const close = useCallback(() => {
    if (!enabled) return;
    const fromList = (location.state as { permalinkFromList?: boolean } | null)?.permalinkFromList;
    if (fromList) navigate(-1);
    else navigate({ pathname: basePath, search: location.search }, { replace: true });
  }, [enabled, navigate, basePath, location.search, location.state]);

  return useMemo(() => ({ open, close }), [open, close]);
}
